/**
 * Contains formatters for the application.
 *
 * File: \src\Formatters.ts
 * Created Date: Tuesday, October 27th 2020, 15:20:28
 * Author: Sascha Herrmann
 * -----
 * Last Modified: May 19th 2025, 11:35:09
 * Modified By: Herrmann Sascha (LDC)
 * -----
 * Copyright (c) 2017-2020 Liebherr-Hausgeräte GmbH
 */

import { App } from 'vue';

import numeral from 'numeral';
import DOMPurify from 'dompurify';
import VueI18n from 'vue-i18n';

import { DateTime, Duration } from 'luxon';
import { isDate, isString } from 'lodash';
import { useI18nStore } from '@/I18nStore';

declare module 'vue' {
  interface ComponentCustomProperties {
    $format: Formatters;
  }

  interface ComponentCustomOptions {
    format?: Formatters;
  }
}

type DateFormatType = Date | DateTime | string;

/**
 * This is used to format.
 *
 * You use it in Typescript by adding:
 * ```
 * let formatted: string = this.$format.temperature(temperature);
 * ```
 * instead of:
 * ```
 * let formatted = this.$options.filters.formatTemperature(temperature);
 * ```
 *
 * The usage in Vue files is also possible (just like $t for example):
 * ```
 * {{ $format.dateTimeShort(message.senddate) }}
 * ```
 * Before:
 * ```
 * {{ message.senddate | formatDateTimeShort }}
 * ```
 *
 * This whole replaces the old filtering mechanism in Vue 2 which no longer exists in Vue 3.
 *
 * @export
 * @class Formatters
 */
export class Formatters {
  public static $format: Formatters;

  /**
   * The ellipsis ("..."), if string is too long to be displayed.
   *
   * @type {string}
   * @memberof Formatters
   */
  public static readonly HELLIP: string = String.fromCharCode(0x2026);

  public static readonly THREE_DAYS_HOURS: number = 72;

  /**
   * Formats to a standard date to 'DateTime.DATE_SHORT' format.
   *
   * @example
   * "29.03.2018" for Germany
   * @example
   * "03/29/2018" for the US
   *
   * @param {(DateFormatType)} value The value to format.
   * @returns {string} A formatted string or '' if empty.
   *
   * @memberof Formatters
   */
  public date(value: DateFormatType): string {
    if (value) {
      let date: DateTime;

      if (isDate(value)) {
        date = DateTime.fromJSDate(value);
      } else if (isString(value)) {
        date = DateTime.fromISO(value);
      } else {
        date = value;
      }

      if (date.isValid) {
        return date.toLocaleString(DateTime.DATE_SHORT);
      }
    }

    return '';
  }

  /**
   * Formats a date value to the "DateTime.TIME_SIMPLE" format.
   *
   * @example
   * "10:18" for Germany
   * @example
   * "10:18 AM" for the US
   *
   * @param {(DateFormatType)} value The value to format.
   * @returns {string} The formatted value or ''.
   *
   * @memberof Formatters
   */
  public time(value: DateFormatType): string {
    if (value) {
      let date: DateTime;

      if (isString(value)) {
        date = DateTime.fromISO(value);
      } else if (isDate(value)) {
        date = DateTime.fromJSDate(value);
      } else {
        date = value;
      }

      if (date.isValid) {
        return date.toLocaleString(DateTime.TIME_SIMPLE);
      }
    }

    return '';
  }

  /**
   * Formats date and time value to the "DateTime.DATETIME_MED" format.
   *
   * @example
   * "27. Sep. 2018 07:54" for Germany
   *
   * @param {(DateFormatType)} The value to format.
   * @returns {string} The formatted value as string.
   *
   * @memberof Formatters
   */
  public dateTime(value: DateFormatType): string {
    if (value) {
      let date: DateTime;

      if (isDate(value)) {
        date = DateTime.fromJSDate(value);
      } else if (isString(value)) {
        date = DateTime.fromISO(value);
      } else {
        date = value;
      }

      if (date.isValid) {
        return this.getFormattedDateTime(date);
      }
    }

    return '';
  }

  /**
   * Formats the date time based on locale
   *
   * @private
   * @param {DateTime} date
   * @return {*}  {string}
   * @memberof Formatters
   */
  public getFormattedDateTime(date: DateTime): string {
    const i18nStore = useI18nStore();
    const languageCode: string = i18nStore.getLoadedLanguageCode;

    switch (languageCode) {
      case 'en-US':
        return `${date.toFormat('MM/dd/yyyy')} ${date.toLocaleString(DateTime.TIME_SIMPLE)}`;
      case 'de-DE':
        return `${date.toFormat('dd.MM.yyyy')} ${date.toLocaleString(DateTime.TIME_SIMPLE)}`;
      case 'en-GB':
      default:
        return `${date.toFormat('dd/MM/yyyy')} ${date.toLocaleString(DateTime.TIME_SIMPLE)}`;
    }
  }

  /**
   * Formats the time information in a short fashion.
   * If it's today's date, we will see a short date format.
   * Otherwise we fall back to formatDateTime's format ("DateTime.DATETIME_MED"):
   *
   * @example
   * Today, 2:39 PM
   *
   * @param {(DateFormatType)} value The value to format.
   * @returns The formatted value as string.
   *
   * @memberof Formatters
   */
  public dateTimeShort(value: DateFormatType): string {
    if (value) {
      let date: DateTime;

      if (isDate(value)) {
        date = DateTime.fromJSDate(value);
      } else if (isString(value)) {
        date = DateTime.fromISO(value);
      } else {
        date = value;
      }

      const endOfDay = date.endOf('day');
      const endOfToday: DateTime = DateTime.local().endOf('day');

      const diffInDays: number = endOfToday.diff(endOfDay, 'days').toObject().days;

      // A future date or later more 1 day difference
      if (diffInDays < 0 || diffInDays > 1) {
        return date.toLocaleString(DateTime.DATETIME_MED);
      }

      let prefix: string;
      const { t } = useI18nStore();

      if (diffInDays === 0) {
        prefix = t('general.today');
      }

      if (diffInDays === 1) {
        prefix = t('general.yesterday');
      }

      return prefix + ', ' + date.toLocaleString(DateTime.TIME_SIMPLE);
    }

    return '';
  }

  /**
   * Formats the given date to a shortened ISO string (10 characters).
   * Used by the date period filter for example.
   *
   * @param {DateTime} date The date to format.
   * @return {*}  {string} The formatted date, cut to the first 10 characters.
   *
   * @memberof Formatters
   */
  public dateISOShort(date: DateTime): string {
    if (!date) {
      return '';
    }

    return date.toISODate();
  }

  /**
   * Formats the given date to the "DateTime.DATETIME_MED" format and appends the timezone that is set in the DateTime object.
   *
   * @example
   * Jun 23, 2022, 12:59 PM (UTC+03:00)
   * for Europe/Sofia timezone in the summer
   *
   * Jun 23, 2022, 11:59 AM (UTC+02:00)
   * for Europe/Berlin timezone in the summer.
   *
   * Jun 23, 2022, 9:59 AM (UTC+00:00)
   * for etc/UTC or UTC
   *
   * @param {DateTime} date The date to format.
   * @return {*}  {string} The formatted date, cut to the first 10 characters.
   *
   * @memberof Formatters
   */
  public dateTimeZone(date: DateTime): string {
    if (!date) {
      return '';
    }

    return this.dateTime(date) + ' (UTC' + date.toFormat('ZZ') + ')';
  }

  /**
   * Formats a temperature for display.
   *
   * @example
   * 24.56432222 => "24.5°C"
   *
   * @param {number} value The value to format.
   * @param {boolean} [unit=true] Include "°C" as unit.
   * @returns {string} The formatted string.
   *
   * @memberof Formatters
   */
  public temperature(value: number, unit: boolean = true): string {
    let formatted: string = '-';

    if (value === null || value === undefined) {
      return formatted + (unit ? '°C' : '');
    }

    formatted = numeral(value).format('0.0') + (unit ? '°C' : '');

    return formatted;
  }

  /**
   * Formats/rounds to one decimal after the comma.
   *
   * @example
   * 3.713 => 3.7
   *
   * @param {number} value The value to format.
   * @returns {number} The formatted number.
   *
   * @memberof Formatters
   */
  public oneDecimal(value: number): number {
    if (value) {
      return +value.toFixed(1);
    }

    return 0;
  }

  /**
   * Formats from milliseconds to minute.
   *
   * @example
   * 120000 => 2
   *
   * @param {number} value The value to format.
   * @returns {number} The formatted number.
   *
   * @memberof Formatters
   */
  public millisToMinute(value: number): number {
    if (value) {
      return +(value / 60000).toFixed(0);
    }

    return 0;
  }

  /**
   * Formats from minute to milliseconds.
   *
   * @example
   * 3 => 180000
   *
   * @param {number} value The value to format.
   * @returns {number} The formatted number.
   *
   * @memberof Formatters
   */
  public minuteToMillis(value: number): number {
    if (value) {
      return value * 60000;
    }

    return 0;
  }

  /**
   * Formats seconds as minutes
   *
   * @param {number} value The value to format.
   * @returns {number} The formatted number.
   * @memberof Formatters
   */
  public secondToMinute(value: number): number {
    if (value) {
      return +(value / 60);
    }

    return 0;
  }

  /**
   *  Formats minutes as seconds
   *
   * @param {number} value
   * @returns {number}
   * @memberof Formatters
   */
  public minuteToSecond(value: number): number {
    if (value) {
      return value * 60;
    }

    return 0;
  }

  /**
   * Formats a number for a small area or a badge.
   * Maximum is 99. After that returns "99+".
   *
   * @param {number} value The value to format.
   * @returns {string} The formatted value as string.
   *
   * @memberof Formatters
   */
  public badgeNumber(value: number): string {
    if (value < 100) {
      return value.toString();
    }

    return '99+';
  }

  /**
   * Format the number of the badge in alarm KPI. Maximum is 9, then reports "9+".
   *
   * @param {number} value The number to format.
   * @returns A formatted value as string.
   *
   * @memberof Formatters
   */
  public smallBadgeNumber(value: number): string {
    if (value < 10) {
      return value.toString();
    }

    return '9+';
  }

  /**
   * Formats the name and cuts them down to a specified length if needed.
   *
   * @param {string} value The value to check.
   * @param {number} [lengthLimit=15] The limit for the length of the string.
   * @returns {string} the - maybe - shortened string.
   * @memberof Formatters
   */
  public shortText(value: string, lengthLimit: number = 15): string {
    if (value?.length > lengthLimit) {
      return value.substring(0, lengthLimit - 1) + Formatters.HELLIP;
    }

    return value;
  }

  /**
   * Use DOMPurify to check the safety of a passed in string.
   *
   * @param {VueI18n.TranslateResult} value The value as a string.
   * @return {*}  {string} The safe string.
   * @memberof Formatters
   */
  public safe(value: VueI18n.TranslateResult): string {
    if (!value) {
      return '';
    }

    return DOMPurify.sanitize(value.toString());
  }

  /**
   * Use DOMPurify to check the safety of a passed in string.
   *
   * @param {string} value The value from vue-i18n
   * @return {*}  {string} The safe string.
   * @memberof Formatters
   */
  public safeString(value: string): string {
    if (!value) {
      return '';
    }

    return DOMPurify.sanitize(value);
  }

  /**
   * Formats a duration in milliseconds to a string.
   *
   * @param {number} value The value to format.
   * @returns {string} The formatted value as string.
   */
  public duration(value: number): string {
    if (!value) {
      return undefined;
    }

    const luxonDuration = Duration.fromMillis(value);

    let durationText = '';

    const dayDurationText: string = luxonDuration.toFormat('d');
    const hoursDurationText: string = luxonDuration.toFormat('h');
    const hours = String(Number(hoursDurationText) - Number(dayDurationText) * 24);
    const minDurationText: string = luxonDuration.toFormat('m');
    const min = String(Number(minDurationText) - Number(hoursDurationText) * 60);
    const secDurationText: string = luxonDuration.toFormat('s');
    const sec: string = String(Number(secDurationText) - Number(minDurationText) * 60);

    //BUS:15453 requirement: After three days have passed, = 71h 59m 59s passes, then switch to format "x"d "y"h "z"m  e.g., 4d 12h 11m
    Number(dayDurationText) * 24 + Number(hours) >= Formatters.THREE_DAYS_HOURS
      ? (durationText += dayDurationText + 'd' + ' ' + hours + 'h' + ' ' + min + 'm')
      : (durationText +=
          String(Number(dayDurationText) * 24 + Number(hours)) + 'h' + ' ' + min + 'm' + ' ' + sec + 's');

    return durationText;
  }
}

export default {
  install: (app: App): void => {
    const formatter = new Formatters();
    app.config.globalProperties.$format = formatter;
    Formatters.$format = formatter;
  }
};
